/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';

/**
 * One entry of an attendance's timeline.
 *
 * The stage keys are `from`/`to`, matching what the backend writes in
 * `deal_model.advance_to`. They were declared here as `from_stage`/
 * `to_stage`, which no payload has ever carried -- harmless only because
 * the sidebar renders `type` alone, so any attempt to show "from X to Y"
 * would have started out undefined.
 *
 * `actor` is who performed the change; the backend falls back to the
 * deal's own broker when no acting user reaches it.
 */
export interface DealEvent {
  type: string;
  timestamp: string;
  stage?: string;
  from?: string;
  to?: string;
  actor?: string;
  note?: string;
  /** The amount recorded by a `proposal_recorded` event. */
  value?: number;
  /** The offer a `proposal_*` event is about. */
  proposal_id?: string;
  /** What a `proposal_answered` event settled the offer as. */
  status?: DealProposalStatus;
  /** Which side a `proposal_countered` event came from. */
  side?: 'client' | 'owner';
  /** The lease opened by a `lease_draft_created` event. */
  lease_id?: string;
  /** The sale record opened by a `sale_record_created` event. */
  sale_id?: string;
}

export interface DealDocument {
  document_id: string;
  filename: string;
  content_type: string;
  upload_date: string;
  file_size?: number;
  download_url?: string;
}

export interface CommissionParticipant {
  party_id: string;
  role: string;
  share_percentage: number;
  payment_source: string;
}

export interface Commission {
  total_percentage: number;
  participants: CommissionParticipant[];
  administration_percentage: number | null;
  arras_type: string | null;
  arras_amount: number | null;
  finalized: boolean;
}

export interface DealOrigin {
  channel: string;
  source_detail: string | null;
  lead_id: string | null;
  campaign: string | null;
}

/** What became of one offer. `refused` and `accepted` are terminal. */
export type DealProposalStatus = 'sent' | 'refused' | 'countered' | 'accepted';

/**
 * One offer put in front of the other side.
 *
 * Carries what the attendance cannot answer for it: which property it is
 * about, whether it is a purchase or a rental, and what happened to it.
 * An attendance may be undecided while the client is; an offer never is.
 */
/** How a rental will be guaranteed. `kind` is an open vocabulary. */
export interface Guarantee {
  kind: string;
  amount?: number | null;
  provider?: string | null;
  monthly_cost?: number | null;
}

export interface RentalTerms {
  guarantee?: Guarantee | null;
  term_months?: number | null;
  start_date?: string | null;
  condominium_paid_by?: 'tenant' | 'landlord' | null;
  iptu_paid_by?: 'tenant' | 'landlord' | null;
  adjustment_index?: string | null;
  occupants?: number | null;
  pets?: boolean | null;
  use?: 'residential' | 'commercial' | null;
}

/** Something the offer waits on before it is a sale. */
export interface ProposalCondition {
  condition_id: string;
  description: string;
  state: 'pending' | 'met' | 'failed';
}

export interface SaleTerms {
  payment_method?: 'cash' | 'financed' | 'fgts' | 'consortium' | 'permuta' | null;
  bank?: string | null;
  down_payment?: number | null;
  financed_amount?: number | null;
  approval?: 'none' | 'pre_approved' | 'approved' | 'denied' | null;
  earnest_amount?: number | null;
  earnest_kind?: 'confirmatorio' | 'penitencial' | null;
  itbi_paid_by?: 'buyer' | 'seller' | null;
  deed_paid_by?: 'buyer' | 'seller' | null;
  handover_days?: number | null;
  conditions?: ProposalCondition[];
}

/** The five known guarantees, offered as suggestions, never as a limit. */
export const KNOWN_GUARANTEE_KINDS = [
  'fiador', 'caucao', 'seguro_fianca', 'titulo_de_capitalizacao', 'sem_garantia',
] as const;

export interface DealProposal {
  proposal_id: string;
  property_id: string;
  deal_type: 'sale' | 'rental';
  value: number;
  status: DealProposalStatus;
  actor?: string | null;
  /** Which side made it. A real negotiation alternates. */
  side?: 'client' | 'owner';
  /** The offer this one answers, when it is a counter. */
  supersedes?: string | null;
  /** Informational only: never changes the status or the stage. */
  valid_until?: string | null;
  rental_terms?: RentalTerms | null;
  sale_terms?: SaleTerms | null;
  created_at: string;
  updated_at: string;
}

export interface Deal {
  deal_id: string;
  agency_id: string;
  deal_type: string;
  sub_type?: string;
  stage: string;
  broker_id: string;
  client_id?: string;
  client_name?: string | null;
  /** The primary property: always `property_ids[0]`, or absent when none. */
  property_id?: string;
  /**
   * Properties this attendance is about, ordered, first being primary.
   * An attendance follows a client: someone who visits a place and does
   * not like it wants to see another, and that is the same work.
   */
  property_ids?: string[];
  property_title?: string | null;
  reference_value?: number;
  /** What the broker put in front of the other side. Set by recordProposal. */
  proposed_value?: number;
  proposed_at?: string;
  notes?: string;
  /** What the broker reads the client as wanting. A guess, not a fact. */
  interest?: 'buy' | 'rent' | 'both' | 'unsure' | null;
  origin?: DealOrigin | null;
  /** One of the closed vocabulary in `constants/lost-reasons.ts`. */
  lost_reason?: string | null;
  /** Free-form context beside the reason; never aggregated. */
  lost_note?: string | null;
  commission?: Commission | null;
  closing_checklist?: Record<string, boolean> | null;
  events: DealEvent[];
  /** The offers made under this attendance, oldest first. */
  proposals?: DealProposal[];
  documents: DealDocument[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Client for the agency deal pipeline (`/agency/{agencyId}/deals`), covering deal
 * listing, creation, stage advancement, and deletion.
 */
/**
 * API client for the deal (negotiation) pipeline: listing, creating, retrieving,
 * advancing through stages, and deleting deals within an agency.
 */
export class DealsService {

  /**
   * Lists deals, one board column at a time when `stages` is given.
   *
   * `stage_counts` totals every stage matching the other filters and
   * ignores the stage selection, so the board renders all five column
   * totals without a count ever depending on the page fetched.
   */
  public static listDeals(
    agencyId: string,
    page: number = 1,
    limit: number = 50,
    stage?: string,
    clientId?: string,
    stages?: readonly string[],
    originChannel?: string,
    brokerId?: string,
    propertyId?: string,
  ): CancelablePromise<{
    data: Deal[];
    pages: number;
    items: number;
    stage_counts: Record<string, number>;
  }> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/deals',
      path: { agencyId },
      query: {
        page,
        page_size: limit,
        stage,
        client_id: clientId,
        property_id: propertyId || undefined,
        stages: stages?.length ? stages.join(',') : undefined,
        origin_channel: originChannel || undefined,
        broker_id: brokerId || undefined,
      },
    });
  }

  public static createDeal(
    agencyId: string,
    body: {
      /**
       * Absent at opening on purpose: an attendance may be about buying
       * or renting while the client is undecided, and the first offer
       * decides it. Pass `interest` instead.
       */
      deal_type?: string;
      /** The broker's reading of what the client wants. Editable. */
      interest?: 'buy' | 'rent' | 'both' | 'unsure';
      broker_id: string;
      client_id?: string;
      property_id?: string;
      sub_type?: string;
      notes?: string;
      reference_value?: number;
    },
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static getDeal(agencyId: string, dealId: string): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/deals/{dealId}',
      path: { agencyId, dealId },
    });
  }

  /**
   * Records the value put in front of the other side.
   *
   * The stage follows the fact: an attendance not yet at `proposal` is
   * carried there. A revised proposal later in the funnel updates the
   * value and leaves the stage alone.
   */
  public static recordProposal(
    agencyId: string,
    dealId: string,
    value: number,
    body: {
      property_id?: string;
      deal_type?: 'sale' | 'rental';
      rental_terms?: RentalTerms;
      sale_terms?: SaleTerms;
      valid_until?: string;
    } = {},
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/proposal',
      path: { agencyId, dealId },
      body: { value, ...body },
      mediaType: 'application/json',
    });
  }

  /**
   * Records an offer from a property page, without the caller having to
   * know which attendance it belongs to.
   *
   * The attendance is found or opened underneath: the broker's own most
   * recent one for that client, unless it is already settled. The
   * response is the attendance the offer landed on, so the screen can
   * link straight through.
   */
  public static recordProposalFromProperty(
    agencyId: string,
    propertyId: string,
    body: {
      client_id: string;
      value: number;
      deal_type: 'sale' | 'rental';
      valid_until?: string;
      rental_terms?: RentalTerms;
      sale_terms?: SaleTerms;
    },
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/properties/{propertyId}/proposals',
      path: { agencyId, propertyId },
      body,
      mediaType: 'application/json',
    });
  }

  /**
   * Records that the broker spoke to the client.
   *
   * Goes on the attendance's timeline rather than into `notes`, which is
   * a single overwritable string with no author and no date. It is also
   * a fact the stage reads: a recorded contact is what takes an
   * attendance out of Novo when there is no property to show yet.
   *
   * The note is the broker's own words and must not be blank -- an empty
   * contact says only that someone pressed a button.
   */
  public static recordContact(
    agencyId: string,
    dealId: string,
    note: string,
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/contacts',
      path: { agencyId, dealId },
      body: { note },
      mediaType: 'application/json',
    });
  }

  /**
   * Answers an offer with a different one, keeping both.
   *
   * A counter is a new offer, not an edit: the answered one keeps
   * everything it said and the new one points back at it, so the chain
   * reads as rounds rather than one record that kept changing.
   */
  public static counterProposal(
    agencyId: string,
    dealId: string,
    proposalId: string,
    body: {
      value: number;
      side?: 'client' | 'owner';
      rental_terms?: RentalTerms;
      sale_terms?: SaleTerms;
    },
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/proposals/{proposalId}/counter',
      path: { agencyId, dealId, proposalId },
      body,
      mediaType: 'application/json',
    });
  }

  /**
   * Records what the world said about one condition.
   *
   * `met` on the last pending one fires the win the acceptance
   * deliberately withheld; `failed` kills the offer and leaves the
   * attendance open for the next.
   */
  public static settleCondition(
    agencyId: string,
    dealId: string,
    proposalId: string,
    conditionId: string,
    state: 'met' | 'failed',
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/deals/{dealId}/proposals/{proposalId}'
        + '/conditions/{conditionId}',
      path: { agencyId, dealId, proposalId, conditionId },
      body: { state },
      mediaType: 'application/json',
    });
  }

  /**
   * Records what the other side said about one offer.
   *
   * Accepting settles the attendance and freezes every other offer as it
   * stood — the others are not marked refused, because an offer that
   * arrived while the client was closing elsewhere was never turned
   * down, it arrived too late.
   */
  public static answerProposal(
    agencyId: string,
    dealId: string,
    proposalId: string,
    status: DealProposalStatus,
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/deals/{dealId}/proposals/{proposalId}',
      path: { agencyId, dealId, proposalId },
      body: { status },
      mediaType: 'application/json',
    });
  }

  /**
   * Removes an offer recorded by mistake.
   *
   * Refused once an offer has been accepted: that acceptance is the
   * attendance's win and opens a Sale or Lease record.
   */
  public static deleteProposal(
    agencyId: string,
    dealId: string,
    proposalId: string,
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/deals/{dealId}/proposals/{proposalId}',
      path: { agencyId, dealId, proposalId },
    });
  }

  public static advanceDeal(
    agencyId: string,
    dealId: string,
    body: {
      target_stage: string;
      transaction_kind?: string;
      lost_reason?: string;
      lost_note?: string;
      accepted_value?: number;
      commission?: { total_percentage: number };
    },
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/advance',
      path: { agencyId, dealId },
      body,
      mediaType: 'application/json',
    });
  }

  public static updateDeal(
    agencyId: string,
    dealId: string,
    body: {
      broker_id?: string;
      client_id?: string;
      /** The broker's reading of what the client wants. Editable. */
      interest?: 'buy' | 'rent' | 'both' | 'unsure';
      /** Promotes this property to primary, keeping the rest of the list. */
      property_id?: string;
      /** Replaces the whole interest list; empty clears the primary too. */
      property_ids?: string[];
      notes?: string;
      reference_value?: number;
      closing_checklist?: Record<string, boolean>;
      /**
       * What the attendance turned out to be. The offers govern it: the
       * server refuses a value any recorded offer disagrees with
       * (422 `deal_type_contradicts_an_offer`), and refuses a null
       * outright — undecided is reachable at birth only.
       */
      deal_type?: 'sale' | 'rental';
    },
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/deals/{dealId}',
      path: { agencyId, dealId },
      body,
      mediaType: 'application/json',
    });
  }

  public static deleteDeal(agencyId: string, dealId: string): CancelablePromise<void> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/deals/{dealId}',
      path: { agencyId, dealId },
    });
  }

  public static pauseDeal(
    agencyId: string,
    dealId: string,
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/pause',
      path: { agencyId, dealId },
    });
  }

  public static resumeDeal(
    agencyId: string,
    dealId: string,
  ): CancelablePromise<Deal> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/resume',
      path: { agencyId, dealId },
    });
  }

  public static getDocuments(
    agencyId: string,
    dealId: string,
  ): CancelablePromise<DealDocument[]> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/deals/{dealId}/documents',
      path: { agencyId, dealId },
    });
  }

  public static getUploadLink(
    agencyId: string,
    dealId: string,
    body: { filename: string; content_type: string },
  ): CancelablePromise<{ document_id: string; filename: string; upload_url: string }> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/deals/{dealId}/documents/upload-link',
      path: { agencyId, dealId },
      body,
      mediaType: 'application/json',
    });
  }

  public static deleteDocument(
    agencyId: string,
    dealId: string,
    documentId: string,
  ): CancelablePromise<void> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/deals/{dealId}/documents/{documentId}',
      path: { agencyId, dealId, documentId },
    });
  }

  public static renameDocument(
    agencyId: string,
    dealId: string,
    documentId: string,
    filename: string,
  ): CancelablePromise<DealDocument> {
    return __request(OpenAPIHousien, {
      method: 'PATCH',
      url: '/agency/{agencyId}/deals/{dealId}/documents/{documentId}',
      path: { agencyId, dealId, documentId },
      body: { filename },
      mediaType: 'application/json',
    });
  }
}
